<template>
  <div>
    <div
      v-if="loading"
      class="text-center"
    >
      <b-spinner
        label="Small Spinner"
        variant="primary"
      />
    </div>

    <b-alert
      variant="danger"
      :show="loadingError ? true : false"
    >
      <div class="alert-body">
        <p>
          {{ loadingError }}
        </p>
      </div>
    </b-alert>

    <!-- Add Capital Supplier Modal -->
    <add-capital-supplier
      v-if="showSupplierForm"
      :edit-id="editSupplierId"
      @modal-closed="closeSupplierForm"
      @supplier-saved="supplierSavedHandler"
    />

    <!-- Add Capital Invoice Modal -->
    <add-capital-invoice
      v-if="showInvoiceForm"
      :edit-id="editInvoiceId"
      @modal-closed="closeInvoiceForm"
      @invoice-saved="invoiceSavedHandler"
    />

    <!-- Supplier List Modal -->
    <b-modal
      v-model="showSupplierListModal"
      title="Capital Suppliers"
      size="lg"
      no-close-on-backdrop
      hide-footer
    >
      <div
        v-if="supplierListLoading"
        class="text-center my-2"
      >
        <b-spinner
          small
          variant="primary"
        />
      </div>
      <b-table
        v-else
        :items="supplierList"
        :fields="supplierTableFields"
        responsive="sm"
        striped
        hover
        class="compact-table"
      >
        <template #cell(GST_applicable)="data">
          {{ data.item.GST_applicable === true || data.item.GST_applicable === 'true' ? 'Yes' : 'No' }}
        </template>
        <template #cell(actions)="data">
          <feather-icon
            icon="EditIcon"
            class="cursor-pointer text-primary"
            size="16"
            @click="editSupplierFromList(data.item)"
          />
        </template>
      </b-table>
    </b-modal>

    <!-- Action Buttons -->
    <b-row>
      <b-col
        align="right"
      >
        <b-button
          variant="outline-info"
          class="mb-1 ml-2"
          @click="openSupplierForm(null)"
        >
          Add Capital Supplier
        </b-button>
        <b-button
          variant="outline-secondary"
          class="mb-1 ml-2"
          @click="openSupplierListModal"
        >
          Supplier List
        </b-button>
        <b-button
          variant="outline-primary"
          class="mb-1 ml-2"
          @click="openInvoiceForm(null)"
        >
          Add Capital Invoice
        </b-button>
      </b-col>
    </b-row>

    <!-- Invoice Table -->
    <b-table
      v-if="!loading && !loadingError"
      :items="results"
      :fields="tableFields"
      responsive="sm"
      striped
      hover
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      class="compact-table"
      no-local-sorting
      @sort-changed="sortingChanged"
    >
      <template #cell(date)="data">
        {{ formatedDate(data.item.date) }}
      </template>
      <template #cell(recieved_date)="data">
        {{ formatedDate(data.item.recieved_date) }}
      </template>
      <template #cell(amount)="data">
        {{ formateNumber(data.item.amount) }}
      </template>
      <template #cell(supplier_name)="data">
        {{ data.item.supplier_detail ? data.item.supplier_detail.name : '-' }}
      </template>
      <template #cell(remarks)="data">
        {{ data.item.remarks || '-' }}
      </template>
      <template #cell(files)="data">
        <span>
          <span
            v-for="(item, index) in data.item.files"
            :key="index"
            class="position-relative"
          >
            <a
              :href="item.file"
              target="_blank"
            >
              <feather-icon
                v-b-tooltip.hover="{boundary:'window'}"
                title="View File"
                icon="FileIcon"
                size="20"
                class="cursor-pointer file-icon"
              />
              <span class="file-number">{{ index + 1 }}</span>
              <span v-if="index !== data.item.files.length - 1">&nbsp;</span>
            </a>
          </span>
        </span>
      </template>
      <template #cell(actions)="data">
        <feather-icon
          icon="EditIcon"
          class="cursor-pointer text-primary"
          size="16"
          @click="openInvoiceForm(data.item.id)"
        />
      </template>
    </b-table>

    <detailed-pagination
      v-if="!loading && !loadingError"
      :total-items="totalItems"
      :per-page="perPage"
      :current-page="currentPage"
      @page-changed="(page) => {currentPage = page}"
    />

  </div>
</template>

<script>
import {
  BSpinner, BTable, BAlert, BRow, BCol, BButton, VBTooltip, BModal,
} from 'bootstrap-vue'
import axios from 'axios'

import dateMixin from '@/mixins/dateMixin'
import formatMixin from '@/mixins/formatMixin'
import DetailedPagination from '@/components/UI/DetailedPagination.vue'
import AddCapitalSupplier from './AddCapitalSupplier.vue'
import AddCapitalInvoice from './AddCapitalGoodsInvoice.vue'

export default {
  name: 'CapitalGoodsInvoiceList',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSpinner,
    BTable,
    BRow,
    BCol,
    BButton,
    BAlert,
    BModal,
    DetailedPagination,
    AddCapitalSupplier,
    AddCapitalInvoice,
  },
  mixins: [dateMixin, formatMixin],
  props: {
    filters: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      loadingError: null,

      // Invoice list
      results: [],
      tableFields: [
        { key: 'invoice_no', label: 'Invoice No', sortable: true },
        { key: 'date', label: 'Invoice Date', sortable: true },
        {
          key: 'amount', label: 'Amount', sortable: true, class: 'text-right pr-2',
        },
        { key: 'total_items', label: 'Total Items', sortable: true },
        { key: 'recieved_date', label: 'Received Date', sortable: true },
        { key: 'supplier_name', label: 'Supplier', sortable: false },
        { key: 'remarks', label: 'Remarks', sortable: false },
        { key: 'created_by_fullname', label: 'Created By', sortable: false },
        { key: 'files', label: 'Files', sortable: false },
        { key: 'actions', label: 'Actions', sortable: false },
      ],
      currentPage: 1,
      perPage: 20,
      totalItems: 0,
      sortBy: 'date',
      sortDesc: true,

      // Supplier form
      showSupplierForm: false,
      editSupplierId: null,

      // Invoice form
      showInvoiceForm: false,
      editInvoiceId: null,

      // Supplier list modal
      showSupplierListModal: false,
      supplierList: [],
      supplierListLoading: false,
      supplierTableFields: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Name' },
        { key: 'address', label: 'Address' },
        { key: 'GST_applicable', label: 'GST Applicable' },
        { key: 'GST_number', label: 'GST Number' },
        { key: 'actions', label: 'Actions' },
      ],
    }
  },
  watch: {
    currentPage() {
      this.fetchItems()
    },
    filters: {
      deep: true,
      handler() {
        if (this.currentPage !== 1) {
          this.currentPage = 1
        } else {
          this.fetchItems()
        }
      },
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy
      this.sortDesc = ctx.sortDesc
      this.fetchItems()
    },

    fetchItems() {
      this.loading = true
      const params = {
        page: this.currentPage,
        per_page: this.perPage,
        ...this.filters,
      }

      // Handle array params (supplier, user) - send multiple params
      const queryParts = []
      Object.keys(params).forEach(key => {
        const value = params[key]
        if (Array.isArray(value)) {
          value.forEach(v => {
            queryParts.push(`${key}=${encodeURIComponent(v)}`)
          })
        } else if (value !== null && value !== undefined && value !== '') {
          queryParts.push(`${key}=${encodeURIComponent(value)}`)
        }
      })

      const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : ''

      axios.get(`/invoices/capital_invoice/${queryString}`)
        .then(res => {
          this.results = res.data.results || res.data
          this.totalItems = res.data.count || this.results.length
          this.loadingError = null
          this.loading = false
        })
        .catch(error => {
          this.loadingError = error?.response?.data?.error || 'Something went wrong'
          this.loading = false
        })
    },

    // Supplier form
    openSupplierForm(id) {
      this.editSupplierId = id
      this.showSupplierForm = true
    },
    closeSupplierForm() {
      this.showSupplierForm = false
      this.editSupplierId = null
      this.fetchItems()
    },
    supplierSavedHandler() {
      this.closeSupplierForm()
      if (this.showSupplierListModal) {
        this.fetchSupplierList()
      }
    },

    // Invoice form
    openInvoiceForm(id) {
      this.editInvoiceId = id
      this.showInvoiceForm = true
    },
    closeInvoiceForm() {
      this.showInvoiceForm = false
      this.editInvoiceId = null
    },
    invoiceSavedHandler() {
      this.closeInvoiceForm()
      this.fetchItems()
    },

    // Supplier list modal
    openSupplierListModal() {
      this.showSupplierListModal = true
      this.fetchSupplierList()
    },
    fetchSupplierList() {
      this.supplierListLoading = true
      axios.get('/invoices/capital_supplier/', {
        params: {
          page: 1,
          per_page: 500,
        },
      })
        .then(res => {
          this.supplierList = res.data.results || res.data
          this.supplierListLoading = false
        })
        .catch(() => {
          this.supplierListLoading = false
        })
    },
    editSupplierFromList(supplier) {
      this.showSupplierListModal = false
      this.openSupplierForm(supplier.id)
    },
  },
}
</script>

<style scoped>
.file-icon{
  transform: scale(1.4, 1.2)
}
.file-number{
  position: absolute;
  top: 2px;
  left: 2px;
  color: black;
  /* border: 2px solid firebrick; */
}
</style>
